import type { QuizOption } from "@shared/types";
import { REQUIRED_QUIZ_FIELDS, isQuizFieldAnswered } from "@shared/quizCompletion";
import { getAgeCardPhoto, type QuizAudience } from "@/lib/quizAudience";
import { ageFromBirthday, ageToBucket, RELATIVE_PHOTO_BUCKETS } from "./age";
import type { ScreenDef } from "./types";

/**
 * The v3 onboarding flow, as data.
 *
 * Seven acts with four fused handoffs, per the design brief:
 *   Act 1 — the mirror: how they meet people now, who they want, the reel
 *   Act 2 — identity: gender, pronouns, orientation, age
 *   Act 3 — personality: table energy, topics, group role
 *   Act 4 — the account, fused into the third handoff
 *   Act 5 — belonging: the welcome moment
 *   Act 6 — the first night: which event they want
 *   Act 7 — the offer
 *
 * Where the design mocked a question that the real quiz already asks, the
 * screen carries `questionId` and pulls its headline and options straight
 * from `data/quiz.ts` — the design's invented wording is deliberately not
 * used. Only two questions here are new (`connectionIntent`,
 * `meetingChannels`); both live in `data/quiz.ts` marked `onboardingOnly`.
 *
 * Experiments: pass a config. `omit` drops screens by id, `transform` gets
 * the last word (reorder, splice in a new screen, swap a layout). Neither the
 * runner nor any screen component needs to change for a variant.
 */

export interface OnboardingFlowConfig {
  /** Screen ids to leave out of this variant. */
  omit?: string[];
  /** Final say over the screen list. Runs after `omit`. */
  transform?: (screens: ScreenDef[]) => ScreenDef[];
}

export interface BuildFlowArgs {
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** ISO birthday, if already answered — the age-range options depend on it. */
  birthday?: string;
  /**
   * Gender identity, if already answered. Only used to pick which faces the
   * age cards show when nothing better is known — a community pick already
   * reaches them through the runtime audience.
   */
  genderIdentity?: unknown;
  /**
   * Whether a session already exists. An account the user has is not a step:
   * the screen is dropped here rather than skipped at runtime, so it never
   * renders, never claims a history entry, and no effect has to race another
   * to move past it.
   */
  isSignedIn?: boolean;
  /**
   * The ad already answered "who do you want to meet" (`?a=` on the landing
   * URL), so the screen asking it is dropped.
   *
   * Dropped rather than pre-filled and skipped: a screen that renders with an
   * answer already on it and then leaves by itself is a flicker with a history
   * entry, and the back gesture lands on it again. The runner mirrors the
   * pinned value into `answers.community` — see `OnboardingFlow` — because the
   * answer is what matching needs, not the screen.
   */
  communityPinned?: boolean;
  config?: OnboardingFlowConfig;
}

const COMMUNITY_PHOTO: Record<string, string> = {
  men: "/img/quiz/quiz-community-male.webp",
  sapphic: "/img/quiz/quiz-community-sapphic.webp",
  mixed: "/img/quiz/quiz-community-mixed.webp",
};

/** Photo thumbs for the table-energy rows, one per dinnerVibe option. */
const VIBE_PHOTO: Record<string, string> = {
  life_of_table: "/img/quiz/quiz-community-male.webp",
  chill_engaged: "/img/quiz/quiz-community-sapphic.webp",
  listener: "/img/quiz/quiz-community-mixed.webp",
};

/**
 * "Who are you mainly interested in meeting?" — the same three communities
 * the app matches on, with the labels the community picker already uses (and
 * which are already translated in every supported language).
 */
function communityOptions(t: BuildFlowArgs["t"]): QuizOption[] {
  return (["men", "sapphic", "mixed"] as const).map((value) => ({
    value,
    label: t(`quiz.stepper.communityIntro.options.${value}.label`),
  }));
}

/**
 * Relative age preferences. The impossible direction is dropped at the edges
 * — nobody under 30 is asked about meeting people younger — which is why this
 * list is derived from the birthday rather than fixed.
 */
function ageRangeOptions(t: BuildFlowArgs["t"], birthday?: string): QuizOption[] {
  const bucket = ageToBucket(ageFromBirthday(birthday));
  const available = bucket ? RELATIVE_PHOTO_BUCKETS[bucket] : undefined;
  return (["younger", "same", "older"] as const)
    .filter((key) => !available || available[key] !== undefined)
    .map((value) => ({
      value,
      label: t(`quiz.stepper.ageIntro.options.${value}.label`),
    }));
}

/**
 * Which set of faces the age cards show, when the user's own answers say
 * something the ad's guess doesn't.
 *
 * Only claims a set where the answer is unambiguous. Someone who picked
 * non-binary, genderqueer, both a man and a woman option, or preferred not to
 * say gets no claim at all — the `mixed` deck is the honest picture of the
 * table they would be seated at, and inferring a binary from a non-binary
 * answer to make a photo match would be worse than not trying.
 */
function audienceFromIdentity(identity: unknown): QuizAudience | undefined {
  const picks = Array.isArray(identity) ? (identity as string[]) : [];
  const woman = picks.includes("cis_woman") || picks.includes("trans_woman");
  const man = picks.includes("cis_man") || picks.includes("trans_man");
  const others = picks.some(
    (p) => !["cis_woman", "trans_woman", "cis_man", "trans_man"].includes(p),
  );
  if (others || (woman && man)) return undefined;
  if (woman) return "sapphic";
  if (man) return "men";
  return undefined;
}

function ageRangePhoto(
  birthday: string | undefined,
  photoAudience: QuizAudience | undefined,
) {
  return (value: string, audience: QuizAudience) => {
    const bucket = ageToBucket(ageFromBirthday(birthday));
    const photoBucket = bucket
      ? RELATIVE_PHOTO_BUCKETS[bucket][value as "younger" | "same" | "older"]
      : undefined;
    // `audience` is the ad segment, which is only ever a guess about someone.
    // Anything the user has actually told us beats it.
    return getAgeCardPhoto(photoAudience ?? audience, photoBucket ?? "3039");
  };
}

export function buildOnboardingFlow({
  t,
  birthday,
  genderIdentity,
  isSignedIn,
  communityPinned,
  config,
}: BuildFlowArgs): ScreenDef[] {
  const identityAudience = audienceFromIdentity(genderIdentity);
  const screens: ScreenDef[] = [
    // ── Act 1 · The mirror ────────────────────────────────────────────────
    {
      id: "intro",
      act: "intro",
      kind: "intro",
      chrome: "bare",
      copyKey: "onboarding.intro",
    },
    {
      // How they meet people today: the situation, asked before the wish.
      id: "meeting-channels",
      act: "mirror",
      kind: "choice",
      field: "meetingChannels",
      questionId: "meetingChannels",
      subtitleKey: "onboarding.meetingChannels.subtitle",
      multi: true,
      required: true,
      layout: "list",
    },
    {
      // Ahead of the reveal, because the reveal is the reel: `StoryWheel` draws
      // its faces from `rt.audience`, and answering here sets it. Asked later,
      // the wheel had nothing to go on but the ad's `a=` param, so every
      // organic visitor watched a generic mix while being told their circle
      // was about to get bigger.
      //
      // Ahead of `connection-intent` rather than between it and the reveal:
      // that pair has to stay adjacent, since the reveal continues whichever
      // sentence is picked there.
      //
      // Also the one screen a targeted ad already knows the answer to:
      // `QuizAudience` is the same "men" | "sapphic" | "mixed" value set that
      // `communityOptions` produces, so an ad-segment arm can drop it. It is
      // `required`, so such an arm has to seed `answers.community` from
      // `getQuizAudience()` as well as `omit` it — omitting alone loses the
      // answer that matching needs most.
      id: "community",
      act: "mirror",
      kind: "choice",
      field: "community",
      options: communityOptions(t),
      titleKey: "onboarding.community.title",
      subtitleKey: "onboarding.community.subtitle",
      blurbKey: "quiz.stepper.communityIntro.options.{value}.blurb",
      layout: "cards",
      required: true,
      autoAdvance: false,
      photoFor: (value) => COMMUNITY_PHOTO[value] ?? COMMUNITY_PHOTO.mixed,
    },
    {
      // Must stay directly before the reveal. The reveal continues whichever
      // option is picked here ("Then let's..."), and a continuation only reads
      // as one when it is adjacent: with another question in between, the
      // headline answers something two screens back and lands as a non
      // sequitur no matter how it is worded.
      id: "connection-intent",
      act: "mirror",
      kind: "choice",
      field: "connectionIntent",
      questionId: "connectionIntent",
      subtitleKey: "onboarding.connectionIntent.subtitle",
      // One pick, advancing on tap. The reveal continues this exact sentence,
      // and a continuation of "everything that fits" is a continuation of
      // nothing in particular: it had to guess by taking the first of several,
      // and the Continue press put a second beat between the sentence and its
      // answer. `storeAsArray` because the field is still `string[]` on save.
      storeAsArray: true,
      required: true,
      layout: "list",
    },
    {
      // The reveal mirrors their strongest pick back at them — `context` swaps
      // the headline via i18next's context suffix, falling back to the base
      // key when we have nothing specific to say.
      id: "reveal",
      act: "mirror",
      kind: "statement",
      chrome: "bare",
      copyKey: "onboarding.reveal",
      // The story lander's turning video disc — this is the first moment the
      // flow shows what a night actually looks like, and stills undersold it.
      visual: "wheel",
      beats: [{ key: "beat1", hasBody: true }],
      // Tolerates both shapes: the screen writes a one-element array, but a
      // draft saved before it narrowed to one pick holds several, and an
      // experiment arm could store a bare string.
      values: (ctx) => {
        const intent = ctx.answers.connectionIntent;
        return {
          context: Array.isArray(intent)
            ? (intent[0] ?? "")
            : typeof intent === "string"
              ? intent
              : "",
        };
      },
      ctaKey: "onboarding.reveal.cta",
    },
    {
      // The screen the control flow never had. It sits before every question
      // about the user: they learn what this is, and what a night looks like,
      // while nothing has been asked of them yet.
      id: "how-it-works",
      act: "mirror",
      kind: "howItWorks",
      chrome: "bare",
      copyKey: "onboarding.howItWorks",
      beats: ["pick", "match", "chat", "show", "keep"],
    },
    {
      id: "city",
      act: "mirror",
      kind: "city",
      field: "location",
      questionId: "location",
    },
    {
      id: "handoff-city",
      act: "mirror",
      kind: "statement",
      chrome: "bare",
      copyKey: "onboarding.handoffCity",
      visual: "pulse",
      beats: [{ key: "beat1" }, { key: "beat2" }],
      // A real button rather than the "tap to continue" hint the other
      // handoffs carry. This one lands right after the city picker, where the
      // reader has just been pressing things: a screen that answers only to a
      // tap on its own copy reads as having stopped responding.
      ctaKey: "onboarding.continue",
      // Only the radar's own label, which is where the city belongs on this
      // screen — the headline names Outclose rather than the city, so there is
      // nothing left to phrase around a name that can still be in flight when
      // the screen opens (it renders the instant the city is tapped).
      values: (ctx) => ({ city: ctx.cityName }),
    },

    // ── Act 2 · Identity ──────────────────────────────────────────────────
    {
      id: "gender-identity",
      act: "identity",
      kind: "choice",
      field: "genderIdentity",
      questionId: "genderIdentity",
      kickerKey: "onboarding.identity.kicker",
      footnoteKey: "onboarding.identity.footnote",
      footnoteIcon: "lock",
      multi: true,
      required: true,
      layout: "list",
    },
    {
      id: "pronouns",
      act: "identity",
      kind: "choice",
      field: "pronouns",
      questionId: "pronouns",
      footnoteKey: "onboarding.pronouns.footnote",
      footnoteIcon: "info",
      multi: true,
      required: true,
      layout: "list",
    },
    {
      // Not optional dressing: the server will not stamp `quizCompletedAt`
      // without it (routes/user/index.ts), and the matching prompt reads it
      // (services/matching.ts). A flow that skipped it produced profiles that
      // could never register as complete.
      id: "sexual-orientation",
      act: "identity",
      kind: "choice",
      field: "sexualOrientation",
      questionId: "sexualOrientation",
      footnoteKey: "onboarding.sexualOrientation.footnote",
      footnoteIcon: "lock",
      multi: true,
      required: true,
      layout: "list",
    },
    {
      id: "birthday",
      act: "identity",
      kind: "birthday",
      field: "birthday",
      questionId: "birthday",
      footnoteKey: "onboarding.birthday.footnote",
    },
    {
      id: "age-range",
      act: "identity",
      kind: "choice",
      field: "ageRangePreferences",
      options: ageRangeOptions(t, birthday),
      titleKey: "onboarding.ageRange.title",
      subtitleKey: "onboarding.ageRange.subtitle",
      blurbKey: "quiz.stepper.ageIntro.options.{value}.blurb",
      layout: "cards",
      multi: true,
      required: true,
      photoFor: ageRangePhoto(birthday, identityAudience),
    },
    {
      id: "handoff-preferences",
      act: "identity",
      kind: "statement",
      chrome: "bare",
      copyKey: "onboarding.handoffPreferences",
      beats: [{ key: "beat1", hasLead: true }, { key: "beat2" }],
      // Same as the city handoff: a button, not a hint. Every screen either
      // side of this one is answered by pressing something at the bottom of
      // the screen, and a lone line of grey text is not that.
      ctaKey: "onboarding.continue",
    },

    // ── Act 3 · Personality ───────────────────────────────────────────────
    {
      id: "dinner-vibe",
      act: "personality",
      kind: "choice",
      field: "dinnerVibe",
      questionId: "dinnerVibe",
      blurbKey: "onboarding.dinnerVibe.blurbs.{value}",
      footnoteKey: "onboarding.dinnerVibe.footnote",
      footnoteIcon: "none",
      layout: "rich",
      required: true,
      photoFor: (value) => VIBE_PHOTO[value] ?? VIBE_PHOTO.listener,
    },
    {
      id: "topics",
      act: "personality",
      kind: "choice",
      field: "conversationTopics",
      questionId: "conversationTopics",
      subtitleKey: "onboarding.topics.subtitle",
      multi: true,
      required: true,
      layout: "list",
    },
    {
      id: "group-role",
      act: "personality",
      kind: "choice",
      field: "groupRole",
      questionId: "groupRole",
      footnoteKey: "onboarding.groupRole.footnote",
      footnoteIcon: "none",
      layout: "rich",
      required: true,
    },

    // ── Act 4 · Account ───────────────────────────────────────────────────
    {
      // Before the offer, never after. Every payment route is `requireAuth()`
      // (server/src/routes/user/payment.ts), so a plan chosen by a signed-out
      // visitor has nothing to attach to and no charge can be started. It also
      // means an abandon after this point is recoverable: there is an account
      // and an address to follow up to.
      id: "account",
      act: "account",
      kind: "auth",
      chrome: "bare",
      copyKey: "onboarding.account",
    },
    // ── Act 5 · Belonging ─────────────────────────────────────────────────
    {
      id: "welcome",
      act: "welcome",
      kind: "welcome",
      chrome: "bare",
      copyKey: "onboarding.welcome",
    },
    {
      // Behind the welcome, not before the account. The control flow asks for
      // a phone number ahead of its auth gate and loses a third of everyone
      // who reaches it; here the account already exists and the welcome has
      // landed, so the ask reads as "how do we reach you about your table"
      // rather than as one more toll on the way in.
      //
      // Reuses the control stepper's copy keys — the two surfaces ask exactly
      // the same thing, and one set of strings means they cannot drift.
      id: "contact",
      act: "welcome",
      kind: "contact",
      copyKey: "quiz.stepper.contact",
    },
    // ── Act 6 · The first night ───────────────────────────────────────────
    {
      // Before the offer, deliberately. The welcome's CTA says "Find my first
      // event", and it used to hand off to the dashboard — which has neither
      // events nor a way to book one. Choosing the night here also changes
      // what the offer means: a plan priced against a table they have already
      // picked, rather than a subscription in the abstract.
      id: "events",
      act: "account",
      kind: "events",
      copyKey: "onboarding.events",
      limit: 5,
    },

    // ── Act 7 · The offer ─────────────────────────────────────────────────
    {
      id: "plans",
      act: "account",
      kind: "plans",
      chrome: "bare",
      copyKey: "onboarding.plans",
      // One-time last on purpose: it's the fallback, not the pitch. Leading
      // with it invites the coin-flip purchase this screen argues against.
      // Ids as the payment-options endpoint serves them (server/data/stripe.ts).
      plans: ["sub_1_month", "sub_3_month", "sub_6_month", "one_time"],
      recommended: "sub_3_month",
    },
  ];

  // Both run before `omit` so an experiment's screen list is what it asked
  // for minus the steps this session has no business showing, not the other
  // way round: an account the user has, and a question the ad already
  // answered, are not screens anyone should have to press through.
  const shown = screens.filter(
    (s) =>
      !(isSignedIn && s.kind === "auth") &&
      !(communityPinned && s.id === "community"),
  );
  const omitted = config?.omit?.length
    ? shown.filter((s) => !config.omit!.includes(s.id))
    : shown;
  return config?.transform ? config.transform(omitted) : omitted;
}

/**
 * The furthest a session without an account may go.
 *
 * Everything past the account screen assumes one exists: the welcome greets
 * people by name, the contact screen writes to a profile, the first-night
 * picker reserves a seat and the offer charges for it. `?step=` is a real
 * entry point — it is how an auth round-trip comes back, and how QA reaches
 * the tail of the flow — so "signed out" and "deep link to the welcome" is a
 * combination that happens, and it has to land on the gate rather than on a
 * screen that has nothing to work with.
 *
 * A no-op for a signed-in session, whose screen list has no gate in it: the
 * account screen is dropped from the flow entirely once a session exists.
 */
export function clampToAccountGate(
  screens: ScreenDef[],
  index: number,
  isSignedIn: boolean,
): number {
  if (isSignedIn) return index;
  const gate = screens.findIndex((s) => s.kind === "auth");
  if (gate < 0) return index;
  return Math.min(index, gate);
}

/**
 * Whether a screen still needs an answer before the flow can be called done.
 *
 * Two lists feed this, and they overlap without being the same: the fields the
 * server stamps `quizCompletedAt` on (`REQUIRED_QUIZ_FIELDS`), and the screens
 * the flow itself marks `required`. The birthday is only in the first — its
 * screen has no `required` flag because the dock gates it by kind — and the
 * pronouns are only in the second. Either one unanswered is a question the
 * user was meant to answer and didn't.
 */
export function screenNeedsAnswer(screen: ScreenDef): boolean {
  if (!("field" in screen) || !screen.field) return false;
  if ((REQUIRED_QUIZ_FIELDS as readonly string[]).includes(screen.field)) {
    return true;
  }
  return "required" in screen && screen.required === true;
}

/**
 * Where a returning user picks the flow back up.
 *
 * Only two answers are ever correct, and which one depends on the session:
 *
 *   signed out  →  the intro, always
 *   signed in   →  the first question they still owe an answer to, or where
 *                  signup left off once none are owed — which is the offer
 *                  once the questions are done
 *
 * The draft answers in `localStorage` are deliberately *not* consulted for a
 * signed-out visitor. They outlive the session that wrote them, so reading
 * them dropped someone with a stale draft straight onto the account screen —
 * a signup form for a flow they had not seen, on a browser that may not even
 * be the same person. The draft still prefills every question; it just no
 * longer decides where the flow opens.
 *
 * Signed in, the account exists and the answers are already on the server, so
 * resuming is continuing rather than guessing. Statements and handoffs carry
 * no answer and are stepped over: a returning user does not need the
 * narrative again.
 *
 * "First unanswered", not "after the last answered". Resuming past the last
 * answer assumes everything before it was answered, and it wasn't always: a
 * double tap on a screen that advances on tap moved the flow two screens and
 * skipped the one in between, and a profile that pre-dates a question never
 * saw it. Either way the server would not stamp the profile complete, the
 * app bounced the user back here on every page, and this opened on the
 * welcome — after every question — so the missing one was never asked again.
 * Landing on it is the only way out of that loop.
 */
export function resolveResumeIndex(
  screens: ScreenDef[],
  answers: Record<string, any>,
  isSignedIn: boolean,
): number {
  if (!isSignedIn) return 0;

  const isAnswered = (screen: ScreenDef): boolean =>
    "field" in screen && !!screen.field && isQuizFieldAnswered(answers[screen.field]);

  let lastAnswered = -1;
  screens.forEach((screen, i) => {
    if (isAnswered(screen)) lastAnswered = i;
  });

  // Nothing answered yet: the intro, so the flow reads as a beginning rather
  // than as a form they are already behind on.
  if (lastAnswered < 0) return 0;

  const firstOwed = screens.findIndex(
    (screen) => screenNeedsAnswer(screen) && !isAnswered(screen),
  );
  if (firstOwed >= 0 && firstOwed < lastAnswered + 1) return firstOwed;

  return Math.min(lastAnswered + 1, screens.length - 1);
}
